import { parseEventPayload } from '@reflex/ui/client/event-utils';
import type { AgentActivityState } from '@reflex/shared';
import type { Agent, AgentActivity, AgentStatus, AxonEventType } from '@/lib/types';

export function assertNever(x: never): never {
  throw new Error(`Unhandled value: ${String(x)}`);
}

export const AGENT_SIGNAL_EVENT_TYPES = [
  'agent.plan',
  'agent.progress',
  'agent.need_input',
  'agent.complete',
  'agent.error',
  'agent.stopped',
  'agent.killed',
  'agent.interrupted',
  'agent.tool_use',
  // Runloop turn protocol — turn boundary signals
  'turn.started',
  'turn.completed',
  'turn.failed',
  'turn.cancelled',
  // The agent resumed its last turn on its own (a background task finished).
  'turn.resumed',
  // Broker background-task tracking: published between turns with the full
  // set of tasks still running; an empty set means the background work is
  // done.
  'background.changed',
  // Devbox lifecycle — suspend/shutdown end the active turn from the UI's
  // perspective even though agent.status stays `running` (suspended devboxes
  // resume on the next user message).
  'devbox.suspended',
  'devbox.shutdown',
  'devbox.failed',
] as const satisfies readonly AxonEventType[];

export type SignalEventType = (typeof AGENT_SIGNAL_EVENT_TYPES)[number];

export const AGENT_SIGNAL_TYPES = new Set<AxonEventType>(AGENT_SIGNAL_EVENT_TYPES);

export function isSignalType(type: AxonEventType): type is SignalEventType {
  return AGENT_SIGNAL_TYPES.has(type);
}

/** Returns `null` when the event doesn't map to a definite activity. */
export function activityFromSignal(type: SignalEventType, payload: unknown): AgentActivity | null {
  switch (type) {
    case 'agent.need_input':
      return 'waiting_input';
    case 'agent.complete':
      return 'completed';
    case 'agent.error': {
      // Broker publishes stderr lines as agent.error with errorType: "stderr".
      // These are informational and should not set activity to error.
      const ep = parseEventPayload<{ errorType?: string }>(payload);
      if (ep?.errorType === 'stderr') return null;
      return 'error';
    }
    case 'agent.interrupted':
      return 'interrupted';
    case 'agent.stopped':
    case 'agent.killed':
      return 'stopped';
    case 'agent.plan':
    case 'agent.tool_use':
      return 'working';
    case 'agent.progress': {
      const p = parseEventPayload<{ status?: string }>(payload);
      return p?.status === 'in_progress' ? 'working' : null;
    }
    case 'turn.started':
    case 'turn.resumed':
      return 'working';
    case 'background.changed':
      return backgroundTaskCount(parseEventPayload<{ active?: unknown }>(payload)?.active) > 0
        ? 'working'
        : 'idle';
    case 'turn.completed':
    case 'turn.failed':
    case 'turn.cancelled':
    case 'devbox.suspended':
    case 'devbox.shutdown':
    case 'devbox.failed':
      return 'idle';
    default:
      return assertNever(type);
  }
}

export function initialActivityFromStatus(status: AgentStatus): AgentActivity {
  switch (status) {
    case 'starting':
      return 'starting';
    case 'running':
      return 'working';
    case 'needs_input':
      return 'waiting_input';
    case 'completed':
      return 'completed';
    case 'interrupted':
      return 'interrupted';
    case 'stopping':
    case 'stopped':
    case 'terminated':
      return 'stopped';
    case 'error':
      return 'error';
    default:
      return assertNever(status);
  }
}

/**
 * Derive the left agent list activity from the durable agent row.
 *
 * A `null` turn state carries no boundary evidence, so the display defaults
 * to idle: only an explicit `working` boundary earns the running spinner.
 * Anything that needs proof of a *finished* turn (the unseen dot, the Unread
 * facet, the inbox count) must use {@link unseenActivityFor} instead of this
 * display default.
 */
export function activityFromDurableAgentState(
  agent: Pick<Agent, 'status' | 'turnState' | 'hasBackgroundWork'>,
): AgentActivity {
  if (agent.status === 'running') {
    if (agent.turnState === 'working') return 'working';
    // The turn ended but the broker still runs background work for the agent
    // (see `agent.hasBackgroundWork`), so the run is not finished yet.
    return agent.hasBackgroundWork === true ? 'working' : 'idle';
  }
  return initialActivityFromStatus(agent.status);
}

/** Number of background tasks in a `background.changed` / stream list. */
export function backgroundTaskCount(tasks: unknown): number {
  return Array.isArray(tasks) ? tasks.length : 0;
}

/**
 * Whether a running agent reads as working only because of the broker's
 * background tasks: its last turn has ended, so there is no turn in flight to
 * interrupt even though {@link activityFromDurableAgentState} says `working`.
 */
export function hasBackgroundOnlyWork(
  agent: Pick<Agent, 'status' | 'turnState' | 'hasBackgroundWork'>,
): boolean {
  return (
    agent.status === 'running' && agent.turnState !== 'working' && agent.hasBackgroundWork === true
  );
}

/**
 * Caption for a running agent whose last turn ended while the broker still
 * runs background work for it, or `null` when there is none. Only a finished
 * turn qualifies: the live turn already reads as working on its own.
 */
export function backgroundWorkLabel(
  agent: Pick<Agent, 'status' | 'turnState' | 'hasBackgroundWork'>,
): string | null {
  if (!hasBackgroundOnlyWork(agent)) return null;
  return 'Working in the background';
}

/**
 * Derive the activity used for unseen/unread semantics (the row dot, the
 * Unread facet, the inbox count). Unlike {@link activityFromDurableAgentState},
 * which is a display default, this requires explicit evidence that a turn
 * finished before a `running` agent counts as idle: either the durable
 * `turnState` says `idle`, or the live `agent:activity` signal does. A
 * `running` agent with a `null` turn state and no live signal counts as
 * working, because `lastActiveAt` advances on in-flight tool calls and the
 * dot must never claim a finished turn without proof. Every unseen/unread
 * consumer derives its activity here, so the dot, the facet, and the count
 * cannot disagree.
 */
export function unseenActivityFor(
  agent: Pick<Agent, 'id' | 'status' | 'turnState'>,
  liveActivities: ReadonlyMap<string, AgentActivityState>,
): AgentActivity {
  if (agent.status === 'running') {
    const turnFinished = agent.turnState === 'idle' || liveActivities.get(agent.id) === 'idle';
    return turnFinished ? 'idle' : 'working';
  }
  return initialActivityFromStatus(agent.status);
}
